import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoNote } from "@/components/shared";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const { activeWorkspace } = useApp();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    if (!activeWorkspace) return;
    try {
      const [{ data: b }, { data: p }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspace.id}/billing`), api.get("/plans"),
      ]);
      setData(b);
      setPlans(p);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(); }, [activeWorkspace?.id]); // eslint-disable-line

  const changePlan = async (planId) => {
    setBusy(planId);
    try {
      const { data: res } = await api.post(`/workspaces/${activeWorkspace.id}/billing/change-plan`, { planId });
      toast.success(res.note || `Switched to ${planId}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  if (!data) return <Skeleton className="h-64 w-full" />;

  const current = data.subscription?.planId || "free";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Billing</h1>
        <p className="text-xs text-muted-foreground">Plan: <span className="font-semibold capitalize">{current}</span> · {data.byok_note}</p>
      </div>

      {!data.stripe_configured && (
        <InfoNote>Payment collection is not active yet (Stripe not configured). Plans can be switched for evaluation - limits are enforced server-side immediately.</InfoNote>
      )}

      <Card className="shadow-none">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Usage</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(data.usage).map(([k, u]) => (
            <div key={k} data-testid={`usage-${k}`}>
              <div className="flex justify-between text-xs">
                <span className="capitalize text-muted-foreground">{k.replaceAll("_", " ")}</span>
                <span className="font-medium tabular-nums">{u.used ?? "—"}{u.limit != null ? ` / ${u.limit}` : ""}</span>
              </div>
              {u.limit != null && u.used != null && <Progress value={Math.min(100, (u.used / u.limit) * 100)} className="mt-1 h-1.5" />}
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        {plans.map((p) => (
          <Card key={p.id} className={`shadow-none ${current === p.id ? "border-primary ring-1 ring-primary/20" : ""}`} data-testid={`billing-plan-${p.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold">{p.name}</span>
                {current === p.id && <Badge variant="outline" className="border-primary/30 bg-primary/5 text-xs text-primary">Current</Badge>}
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">{p.price_monthly === 0 ? "$0" : "TBA"}<span className="text-xs font-normal text-muted-foreground"> /mo</span></div>
              <div className="text-xs text-muted-foreground">{p.price_note}</div>
              <ul className="mt-3 space-y-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{f}
                  </li>
                ))}
              </ul>
              {current !== p.id && (
                <Button size="sm" variant="outline" className="mt-4 w-full" disabled={busy === p.id} onClick={() => changePlan(p.id)} data-testid={`billing-switch-${p.id}`}>
                  {busy === p.id && <Loader2 className="mr-2 h-3 w-3 animate-spin" />} Switch to {p.name}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
